<template>
  <div>
    <section-title :content="`${normativeGroup?.name}:`" has-line />
    <validation-provider v-slot="{ errors }" name="groups" rules="checks">
      <options-panel
        v-model="normativeGroup.options"
        class="mx-3"
        :data="{ options: normativeGroup?.options || [] }"
        :has-container-panel="false"
        :is-read-only="isReadOnly"
        is-vertical
        option-col="col-4 py-3"
        :show-header="false"
      />
      <error
        v-if="errors[0]"
        class="mt-3"
        message="Seleccione al menos un grupo"
        variant="no-border"
      />
    </validation-provider>
    <div v-if="!isReadOnly" class="my-3">
      <div class="col d-flex align-items-end">
        <custom-drop-down
          v-model="moduleSelected"
          class="w-40"
          :clearable="false"
          fieldkey="id"
          fieldname="name"
          is-html
          label="Prueba/Muestra - Módulo:"
          :options="modules"
          placeholder="Seleccione"
        />
        <action-button
          class="ml-2"
          :disabled="!moduleSelected"
          i="fas fa-file-plus"
          variant="success"
          @click="addModule()"
        >
          Añadir
        </action-button>
      </div>
      <validation-provider
        ref="modules"
        v-slot="{ errors }"
        name="modules"
        :rules="answers.length > 0 ? '' : 'required'"
      >
        <error
          v-if="errors[0]"
          class="m-3"
          message="Añada al menos un módulo"
          variant="no-border"
        />
      </validation-provider>
    </div>
    <div class="m-3">
      <div
        v-if="answers?.length"
        class="d-flex justify-content-end align-items-center"
      >
        <action-button
          class="outline-dark-gray"
          :i="allExpanded ? 'fal fa-compress-alt' : 'fal fa-expand-alt'"
          :text="allExpanded ? 'Colapsar Todo' : 'Expandir Todo'"
          @click="toggleExpand"
        />
      </div>
      <div class="mx-3">
        <custom-collapse id="answers-collapse">
          <custom-collapse-item
            v-for="item in answers"
            :id="`answer-item-${item.id}`"
            :key="`answer-item-${item.id}`"
            :ref="`answer-item-${item.id}`"
            :auto-expand-collapse-enabled="false"
            button-icon="far fa-times-circle color-red"
            class="my-3 collapse-item section bg-gray"
            :class="{ 'rounded invalid': getAnswerForm(item)?.invalid }"
            :disabled="isReadOnly"
            :expanded="item.expanded"
            expanded-icon="fas fa-plus-circle custom-reorganize-btn"
            icon-class-when-button-disabled="fas fa-grip-vertical flex-fill d-flex justify-content-end align-items-center"
            :keep-btn-visible="false"
            parent-id="answers-collapse"
            read-only-icon="fas fa-plus-circle color-gray-2"
            :title="getAnswerTitle(item)"
            @actionButtonClick="quit(item)"
            @expandButtonClick="expand(item)"
          >
            <answer
              :ref="`answer-form-${item.id}`"
              :is-read-only="isReadOnly"
              :value="item"
              @input="onAnswer(item, $event)"
            />
          </custom-collapse-item>
        </custom-collapse>
      </div>
    </div>
    <div class="m-3">
      <label class="font-weight-bold">
        Interpretación de las pruebas VALPAR administradas:
      </label>
      <p v-if="isReadOnly">{{ model.comment | empty }}</p>
      <validation-provider v-else v-slot="{ errors }" rules="required">
        <custom-text-area
          v-model="model.comment"
          :count-classes="['d-block', 'small-counter', 'text-right']"
          custom-error-msg="El campo es requerido."
          :error="errors[0]"
          :maxlength="5000"
          show-char-count
        />
      </validation-provider>
    </div>
  </div>
</template>

<script>
import alertMixin from '@/mixins/alert.mixin.js';

import Answer from './components/AreaAnswer.vue';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import ActionButton from 'ActionButton';
import CustomTextArea from 'CustomTextArea';
import Error from 'custom-error';
import OptionsPanel from '../../../../components/OptionsPanel.vue';
import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';

import { extend } from 'vee-validate';
extend('checks', {
  validate: (value) => value?.filter((x) => x.checked)?.length > 0,
});
const normativeGroupId = 22;

export default {
  name: 'WorkSampleExecution',
  components: {
    SectionTitle,
    CustomDropDown,
    ActionButton,
    CustomTextArea,
    Error,
    OptionsPanel,
    Answer,
    CustomCollapse,
    CustomCollapseItem,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      moduleSelected: null,
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    modules() {
      return this.model.options.filter((x) => !x.selected);
    },
    answers() {
      return this.model.options.filter(
        (x) => x.selected && x.id !== normativeGroupId
      );
    },
    normativeGroup() {
      return this.model.options.find((x) => x.id === normativeGroupId);
    },
    allExpanded: {
      get() {
        return this.answers.every((x) => x.expanded);
      },
      set(value) {
        this.answers.forEach((x) => (x.expanded = value));
      },
    },
    getAnswerForm() {
      return (item) => this.$refs[`answer-form-${item.id}`]?.[0];
    },
    getAnswerTitle() {
      return (item) =>
        `<span class="font-weight-normal">Prueba/Muestra Módulo:</span> ${item.name}`;
    },
  },
  mounted() {
    this.allExpanded = true;
    this.$emit('update:initialModel', this.model);
  },
  methods: {
    toggleExpand() {
      this.allExpanded = !this.allExpanded;
    },
    addModule() {
      if (!this.moduleSelected) return;
      this.moduleSelected.selected = true;
      this.moduleSelected.options
        .filter((x) => x.isOther)
        .forEach((x) => (x.checked = true));
      this.expand(this.moduleSelected, true);

      this.$refs.modules.setErrors({ modules: null });
      this.moduleSelected = null;
    },
    onAnswer(item, value) {
      const index = this.model.options.findIndex((a) => a.id === item.id);
      if (index !== -1) this.model.options[index] = value;
    },
    expand(item, expanded = !item.expanded) {
      this.answers
        .filter((x) => x.id !== item.id)
        .forEach((x) => (x.expanded = false));
      setTimeout(() => (item.expanded = expanded), 100);
    },
    quit(item) {
      item.options.forEach((x) => {
        x.checked = false;
        x.other = null;
      });
      item.expanded = false;
      item.selected = false;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .container-panel {
  outline: none;
}
::v-deep .options-vertical {
  background-color: white;
  padding: 0px 12px;
}
::v-deep .input-group .option-input:checked:disabled + label span {
  background-color: #3f5b60 !important;
}
::v-deep .input-group .option-input:checked:disabled + label span .ic-check {
  background-image: url('../../../../../../../assets/img/white-check.svg') !important;
  background-size: 70% !important;
  background-repeat: no-repeat;
  background-position: center;
}
.outline-dark-gray {
  color: #3f5b60;
  background-color: #ffffff;
  border-color: #3f5b60;
}

.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.outline-dark-gray {
  color: #fff;
  background-color: #3f5b60;
  border-color: #3f5b60;
}
</style>
